import React, { useEffect, useRef, useState } from 'react';
import { expertise } from '../data/mock';
import { Code2, Layout, Smartphone, Database, Cloud } from 'lucide-react';

const iconMap = {
  'Back-end & APIs': Code2,
  'Front Web': Layout,
  'Mobile': Smartphone,
  'Data & IA': Database,
  'Déploiement': Cloud,
};

const Expertise = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="expertise" ref={sectionRef} className="relative py-32 bg-[#0a0a0b] overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#3dd3ee]/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#fad24b]/10 rounded-full blur-[120px]" />
        <div 
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div
          className={`text-center mb-20 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 backdrop-blur-xl rounded-full border border-white/10 text-sm font-medium text-white/70 mb-6">
            <span className="w-2 h-2 rounded-full bg-[#fad24b] animate-pulse" />
            Développement complet
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Expertise{' '}
            <span className="bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(135deg, #3dd3ee, #fad24b)' }}>
              Fullstack
            </span>
          </h2>
          <p className="text-xl text-white/60 max-w-2xl mx-auto">
            Un seul partenaire pour livrer : IA + API + Front + Mobile + Déploiement
          </p>
        </div>

        {/* Expertise Cards */}
        <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4">
          {expertise.map((item, index) => {
            const Icon = iconMap[item.category];
            const isHovered = hoveredIndex === index;

            return (
              <div
                key={item.category}
                className={`group transition-all duration-700 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                <div
                  className={`relative p-6 rounded-2xl border transition-all duration-500 h-full backdrop-blur-xl ${
                    isHovered
                      ? 'bg-white/10 border-white/20 scale-105 shadow-2xl'
                      : 'bg-white/5 border-white/10 hover:bg-white/10'
                  }`}
                >
                  {/* Icon */}
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-all duration-500"
                    style={{ backgroundColor: `${item.color}20` }}
                  >
                    <Icon
                      className="w-6 h-6 transition-transform duration-500 group-hover:scale-110"
                      style={{ color: item.color }}
                    />
                  </div>

                  {/* Category */}
                  <h3 className="text-lg font-bold text-white mb-4">{item.category}</h3>

                  {/* Items */}
                  <ul className="space-y-2">
                    {item.items.map((tech, i) => (
                      <li
                        key={tech}
                        className={`text-sm text-white/60 transition-all duration-300 ${
                          isHovered ? 'translate-x-1 text-white/80' : ''
                        }`}
                        style={{ transitionDelay: `${i * 50}ms` }}
                      >
                        {tech}
                      </li>
                    ))}
                  </ul>

                  {/* Bottom accent */}
                  <div
                    className="absolute bottom-0 left-0 h-1 rounded-b-2xl transition-all duration-500"
                    style={{
                      width: isHovered ? '100%' : '0%',
                      backgroundColor: item.color,
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom Message */}
        <div
          className={`mt-16 text-center transition-all duration-1000 delay-500 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="inline-flex items-center gap-4 px-6 py-4 bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10">
            <p className="text-white/70">
              <span className="text-white font-semibold">Réduction des frictions</span>, meilleure vitesse de delivery.
              Nous livrons des solutions{' '}
              <span className="bg-clip-text text-transparent font-semibold" style={{ backgroundImage: 'linear-gradient(135deg, #3dd3ee, #fad24b)' }}>
                utilisables
              </span>
              , pas des POC isolés.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Expertise;
